import React, { useEffect, useState } from 'react';
import { collection, getDocs, limit, orderBy, query, startAfter } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { formatDateTODMYHMA } from '../../utils/utils';
import { CustomLoader } from '../common/CustomLoader';



export default function BlogPage() {

  const [posts, setPosts] = useState([]);
  const [lastVisible, setLastVisible] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const limitPosts = 10;

  useEffect(() => {
    const fetchPosts = async () => {
      setInitialLoading(true);
      try {
        const postsQuery = query(
          collection(db, 'blog'),
          orderBy('createdDateTime', 'desc'),
          limit(limitPosts)
        );

        const querySnapshot = await getDocs(postsQuery);
        const fetchedPosts = querySnapshot.docs.map(doc => ({
          ...doc.data(),
        }));

        setPosts(fetchedPosts);
        setLastVisible(querySnapshot.docs[querySnapshot.docs.length - 1]);

        if (querySnapshot.docs.length < limitPosts) {
          setHasMore(false); // No more posts available
        }

        setInitialLoading(false);
      } catch (error) {
        console.error("Error fetching posts: ", error);
        setInitialLoading(false);
        // You could also set an error state here to show to the user if needed
      } finally {
        setInitialLoading(false);
      }
    };

    fetchPosts();
  }, []);

  const loadMorePosts = async () => {
    if (!lastVisible || loading) return;

    setLoading(true);
    try {
      const nextQuery = query(
        collection(db, 'blog'),
        orderBy('createdDateTime', 'desc'),
        limit(limitPosts),
        startAfter(lastVisible)
      );

      const querySnapshot = await getDocs(nextQuery);
      const fetchedPosts = querySnapshot.docs.map(doc => ({
        ...doc.data(),
      }));

      setPosts(prevPosts => [...prevPosts, ...fetchedPosts]);
      setLastVisible(querySnapshot.docs[querySnapshot.docs.length - 1]);

      if (querySnapshot.docs.length < limitPosts) {
        setHasMore(false); // No more posts available
      }

      setLoading(false);
    }
    catch (error) {
      console.error("Error fetching posts: ", error);
      setLoading(false);
    }
    finally {
      setLoading(false);
    }
  };

  // Function to parse text and render clickable links
  const parseTextWithLinks = (text) => {
    // Regular expression to match the [name: Link] pattern
    const regex = /\[([^\]]+): ([^\]]+)\]/g;
    let parts = [];
    let lastIndex = 0;

    // Find all matches of the pattern and split text accordingly
    let match;
    while ((match = regex.exec(text)) !== null) {
      // Push the text before the match
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }

      // Push the button with the name and the link
      (function (match) {
        parts.push(
          <a
            key={match.index}
            href={match[2]}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-bright-green text-white
                  font-semibold p-2 shadow-md rounded-lg
                  hover:bg-white hover:text-bright-green transition duration-300"

          >
            {match[1]}
          </a>
        );
      })(match);

      lastIndex = regex.lastIndex;
    }

    // Push the remaining text after the last match
    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }

    return parts;
  };


  return (
    <div className="p-4">
      {
        initialLoading
          ? (renderShimmerEffect())
          : posts.length === 0 ? (
            <p>No Posts Found.</p>
          ) : (
            posts.map(post => (
              <div key={post.id} className="mb-6 p-4 border rounded-lg shadow">
                <h2 className="text-xl font-semibold text-bright-green">{post.title}</h2>
                <p className="text-dark-green mb-2">{formatDateTODMYHMA(post.createdDateTime)}</p>
                <p className="mb-4 text-dark-text-color leading-[2.4]">
                  {parseTextWithLinks(post.text)}
                </p>

                {post.images && post.images.length > 0 && (
                  <div className="grid grid-cols-2 gap-2">
                    {post.images.map((imageUrl, index) => (
                      <img
                        key={index}
                        src={imageUrl}
                        alt={`Blog`}
                        className="w-full h-auto rounded"
                      />
                    ))}
                  </div>
                )}
              </div>
            ))
          )}

      {loading ? (
        <div className="flex justify-center mt-4">
          <CustomLoader />
        </div>
      ) : (
        hasMore && (
          <div className="flex justify-center mt-4">
            <button
              onClick={() => loadMorePosts()}
              className="bg-bright-green text-white
                  font-semibold px-6 py-3 rounded-full shadow-md 
                  hover:bg-white hover:text-bright-green transition duration-300"
            >
              Load More
            </button>
          </div>
        )
      )}

      {!hasMore && !loading && (
        <div className="flex justify-center mt-4">
          <p className="text-light-text-color">No more posts available</p>
        </div>
      )}
    </div>
  );
};


const renderShimmerEffect = () => {
  return (
    <div className="space-y-4">
      {Array.from({ length: 3 }).map((_, index) => (
        <div key={index} className="p-4 border rounded-lg shadow animate-pulse">
          <div className="h-6 bg-gray-300 rounded w-3/4 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
          <div className="h-40 bg-gray-100 rounded"></div>
        </div>
      ))}
    </div>
  );
};